import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { BUSINESS } from '../../constants/business'

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/reviews', label: 'Reviews' },
  { to: '/blog', label: 'Blog' },
]

export default function Navbar() {
  const [isOpen, setIsOpen]     = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const { pathname } = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => { setIsOpen(false) }, [pathname])
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/95 backdrop-blur-sm shadow-xl' : 'bg-[#111111]'
      }`}
      role="banner"
    >
      {/* Thin brown accent line */}
      <div className="h-0.5 bg-brand-brown" />

      <div className="container-custom">
        <div className="flex items-center justify-between h-18 py-2">
          {/* Logo — bigger */}
          <Link
            to="/"
            className="flex items-center gap-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-brown rounded"
            aria-label="Rob Baker's Garage — Home"
          >
            <img
              src="/photos/robbakersgarage-logo.png"
              alt="Rob Baker's Garage logo"
              className="w-14 h-14 object-contain rounded-full"
            />
            <div className="leading-tight">
              <span className="font-heading text-white text-lg font-bold tracking-wide block">ROB BAKER&apos;S GARAGE</span>
              <span className="text-brand-stone/60 text-[10px] uppercase tracking-widest block mt-0.5">Millbrae, CA · Est. 1972</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
            {NAV_LINKS.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`px-3 py-2 text-sm font-medium rounded transition-colors ${
                  pathname === to
                    ? 'text-white bg-brand-brown/50'
                    : 'text-brand-stone hover:text-white hover:bg-white/10'
                }`}
                aria-current={pathname === to ? 'page' : undefined}
              >
                {label}
              </Link>
            ))}
          </nav>

          {/* Desktop right */}
          <div className="hidden md:flex items-center gap-3">
            <a href={BUSINESS.facebookUrl} target="_blank" rel="noopener noreferrer"
              className="text-brand-stone/50 hover:text-white transition-colors" aria-label="Facebook">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
              </svg>
            </a>
            <a href={BUSINESS.instagramUrl} target="_blank" rel="noopener noreferrer"
              className="text-brand-stone/50 hover:text-white transition-colors" aria-label="Instagram">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
              </svg>
            </a>
            <div className="w-px h-5 bg-white/15" />
            <a
              href={`tel:${BUSINESS.phone}`}
              className="inline-flex items-center gap-2 bg-brand-brown text-white px-4 py-2 rounded text-sm font-heading font-bold uppercase tracking-wide hover:bg-brand-rust transition-colors min-h-[40px]"
              aria-label={`Call ${BUSINESS.phoneFormatted}`}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012.18 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.15a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
              </svg>
              {BUSINESS.phoneFormatted}
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsOpen(v => !v)}
            className="md:hidden flex items-center justify-center w-10 h-10 text-white rounded"
            aria-expanded={isOpen}
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            {isOpen
              ? <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>
              : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="M3 12h18M3 6h18M3 18h18"/></svg>
            }
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-[#111111] border-t border-white/10">
          <nav className="container-custom py-4 flex flex-col gap-1">
            {NAV_LINKS.map(({ to, label }) => (
              <Link key={to} to={to}
                className={`px-4 py-3 text-base font-medium rounded transition-colors ${
                  pathname === to ? 'text-white bg-brand-brown/40' : 'text-brand-stone hover:text-white hover:bg-white/10'
                }`}
                aria-current={pathname === to ? 'page' : undefined}
              >
                {label}
              </Link>
            ))}
            <div className="flex items-center gap-4 px-4 pt-3 pb-1">
              <a href={BUSINESS.facebookUrl} target="_blank" rel="noopener noreferrer" className="text-brand-stone/50 hover:text-white transition-colors" aria-label="Facebook">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>
              </a>
              <a href={BUSINESS.instagramUrl} target="_blank" rel="noopener noreferrer" className="text-brand-stone/50 hover:text-white transition-colors" aria-label="Instagram">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
                </svg>
              </a>
            </div>
            <a href={`tel:${BUSINESS.phone}`}
              className="mt-2 flex items-center justify-center gap-2 bg-brand-brown text-white px-4 py-3 rounded font-heading font-bold uppercase tracking-wide hover:bg-brand-rust transition-colors min-h-[48px]">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012.18 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.15a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
              </svg>
              Call {BUSINESS.phoneFormatted}
            </a>
          </nav>
        </div>
      )}
    </header>
  )
}
